import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { handleApiError, formatCurrency, formatDateTime, formatCPF, formatPhone, brMonthStr } from "@/lib/api";
import { toast } from "sonner";

/**
 * Importação de colaboradores pelo último CNAB240 de folha (SPEC-ONBOARD-CNAB
 * v0.6, fase 1, PR B). Contrato: `POST /api/employees/import/cnab/preview` e
 * `POST /api/employees/import/cnab/apply` (routes/employee_import.py).
 *
 * PII (R-P): o arquivo, o diff e o CPF em claro do item `novo` vivem SÓ no
 * estado deste componente (memória da aba). Nada vai para URL, localStorage,
 * sessionStorage nem console. Na tela o CPF aparece só como `cpf_mascarado`
 * (vem do servidor); o `cpf` em claro do item `novo` serve apenas para
 * pré-preencher o cadastro manual de um item `pendente`.
 *
 * Copy: UX/copy é fronteira do owner. Os textos marcados `// SPEC` são os
 * literais da SPEC (ou a copy já existente no cadastro manual, para o mesmo
 * campo); os marcados `// COPY-PENDENTE-OWNER` são rótulos neutros à espera
 * do texto do owner. Motivos, marcas e estados do contrato são mostrados como
 * estão (códigos crus).
 */

/** Teto de decisões por chamada de `apply` (SPEC E2; o servidor recusa > 20). */
export const BLOCO_APPLY = 20;

const T_NOVO = "novo";
const T_SAIDA = "saida_sugerida";

const COPY = {
  botaoAbrir: "Importar último CNAB", // SPEC (Escopo §6)
  titulo: "Importar último CNAB", // COPY-PENDENTE-OWNER
  descricao: "Escolha o arquivo de remessa CNAB240 de folha e a competência do líquido.", // COPY-PENDENTE-OWNER
  notaArquivo: "O arquivo não é guardado: fechar esta janela antes de aplicar exige escolher o arquivo de novo.", // COPY-PENDENTE-OWNER (nota fixa P7, texto não fixado)
  arquivo: "Arquivo CNAB240", // COPY-PENDENTE-OWNER
  competencia: "Competência do líquido", // COPY-PENDENTE-OWNER
  gerarComparativo: "Comparar com o cadastro", // COPY-PENDENTE-OWNER
  trocarArquivo: "Escolher outro arquivo", // COPY-PENDENTE-OWNER
  aviso: "Aviso", // COPY-PENDENTE-OWNER
  jaAplicadoEm: "ja_aplicado_em", // código do contrato, mostrado como está
  resumo: "Resumo", // COPY-PENDENTE-OWNER
  colTipo: "Tipo", // COPY-PENDENTE-OWNER
  colNome: "Nome", // COPY-PENDENTE-OWNER
  colCpf: "CPF", // COPY-PENDENTE-OWNER
  colAntes: "Antes", // COPY-PENDENTE-OWNER
  colDepois: "Depois", // COPY-PENDENTE-OWNER
  colAvisos: "Marcas / bloqueio", // COPY-PENDENTE-OWNER
  colCompletar: "Completar", // COPY-PENDENTE-OWNER
  email: "E-mail", // COPY-PENDENTE-OWNER
  phone: "Celular", // SPEC (mesmo rótulo do cadastro manual)
  salary: "Salário mensal (R$)", // SPEC (mesmo rótulo do cadastro manual)
  department: "Departamento", // COPY-PENDENTE-OWNER
  bankType: "Tipo de Conta", // SPEC (mesmo rótulo do cadastro manual)
  massaTitulo: "Completar em massa nos novos selecionados", // COPY-PENDENTE-OWNER
  massaAplicar: "Aplicar às linhas selecionadas", // COPY-PENDENTE-OWNER
  dataSaida: "Data de saída", // COPY-PENDENTE-OWNER
  faltando: "faltando", // COPY-PENDENTE-OWNER
  enviarEmail: "Enviar notificação de cadastro por email", // SPEC (mesma copy do cadastro manual)
  aplicar: (n) => `Aplicar selecionados (${n})`, // COPY-PENDENTE-OWNER
  aplicando: (feitos, total) => `Aplicando bloco ${feitos} de ${total}`, // COPY-PENDENTE-OWNER
  cancelar: "Cancelar", // SPEC (mesma copy do cadastro manual)
  fechar: "Fechar", // COPY-PENDENTE-OWNER
  resultado: "Resultado", // COPY-PENDENTE-OWNER
  colEstado: "Estado", // COPY-PENDENTE-OWNER
  colMotivo: "Motivo", // COPY-PENDENTE-OWNER
  colCampos: "Campos", // COPY-PENDENTE-OWNER
  cadastrarManual: "cadastrar manualmente", // SPEC (Escopo §6, P2)
  naoEnviados: (n) => `${n} decisão(ões) não enviada(s): o envio parou no erro abaixo.`, // COPY-PENDENTE-OWNER
  pendenciasArquivo: "Pendências do arquivo", // COPY-PENDENTE-OWNER
  erroPreview: "Erro ao gerar o comparativo", // COPY-PENDENTE-OWNER
  erroApply: "Erro ao aplicar", // COPY-PENDENTE-OWNER
  erroArquivo: "Não foi possível ler o arquivo", // COPY-PENDENTE-OWNER
};

/** Rótulo por tipo de item do contrato. */
const ROTULO_TIPO = {
  novo: "Novo", // COPY-PENDENTE-OWNER
  saida_sugerida: "Saída sugerida", // COPY-PENDENTE-OWNER
  liquido: "Mudança de líquido", // COPY-PENDENTE-OWNER
  bruto_divergente: "Divergência de bruto (só sinal)", // COPY-PENDENTE-OWNER
  dados_bancarios: "Dados bancários", // COPY-PENDENTE-OWNER
  nome_divergente: "Nome divergente (só sinal)", // COPY-PENDENTE-OWNER
  pj_fora_da_fase_1: "pj_fora_da_fase_1", // código do contrato, mostrado como está
};

const BANK_TYPE_OPTIONS = [
  { value: "CC", label: "Conta Corrente" }, // SPEC (mesma copy do cadastro manual)
  { value: "CP", label: "Conta Poupança" }, // SPEC (mesma copy do cadastro manual)
  { value: "SA", label: "Conta Salário" }, // SPEC (mesma copy do cadastro manual)
];

/** Campos que o servidor exige no complemento do `novo` (COMPLEMENTO_NOVO do backend). */
const COMPLEMENTO_OBRIGATORIO = ["email", "phone", "department", "salary", "bank_type"];

/** Rótulos legíveis das chaves de campo (owner C1012472); chave desconhecida aparece como está. */
const ROTULO_CAMPO = { email: "E-mail", phone: "Celular", department: "Departamento", salary: "Salário mensal", bank_type: "Tipo de conta" };
export const rotuloCampo = (c) => (Object.prototype.hasOwnProperty.call(ROTULO_CAMPO, c) ? ROTULO_CAMPO[c] : c);

const COMPLEMENTO_VAZIO = { email: "", phone: "", salary: "", department: "", bank_type: "" };

/**
 * "3.500,00" | "3.500" | "1.234.567" | "3500.5" | "" ⇒ número ou null (o
 * servidor decide `pendente`). Com vírgula, o ponto é milhar; sem vírgula, o
 * ponto só é milhar se agrupar de 3 em 3 ("3.500" = 3500), senão é decimal.
 */
export function parseValorBR(texto) {
  const t = String(texto ?? "").trim();
  if (!t) return null;
  let normal = t;
  if (t.includes(",")) normal = t.replace(/\./g, "").replace(",", ".");
  else if (/^\d{1,3}(\.\d{3})+$/.test(t)) normal = t.replace(/\./g, "");
  const n = Number(normal);
  return Number.isFinite(n) ? n : null;
}

/** Divide em blocos de até `tamanho` (SPEC E2). */
export function emBlocos(lista, tamanho = BLOCO_APPLY) {
  const out = [];
  for (let i = 0; i < lista.length; i += tamanho) out.push(lista.slice(i, i + tamanho));
  return out;
}

/** Faltantes do complemento, com a mesma régua do servidor (só para mostrar). */
export function faltantesDoComplemento(comp) {
  return COMPLEMENTO_OBRIGATORIO.filter((campo) => {
    if (campo === "salary") {
      const v = parseValorBR(comp?.salary);
      return v === null || v <= 0;
    }
    return !String(comp?.[campo] ?? "").trim();
  });
}

/**
 * Formulário do cadastro manual pré-preenchido EM MEMÓRIA a partir do item
 * `novo` do preview (SPEC Escopo §6): `payment_method: "bank"` porque o
 * `emptyForm` abre em `pix` e o formulário só mostra os campos bancários fora
 * de pix.
 */
export function prefillCadastroManual(item) {
  const depois = item?.depois || {};
  return {
    cpf_cnpj: formatCPF(item?.cpf || ""),
    name: item?.nome || "",
    bank_code: depois.bank_code || "",
    bank_agency: depois.bank_agency || "",
    bank_account: depois.bank_account || "",
    payment_method: "bank",
  };
}

function lerComoBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result || "");
      const virgula = url.indexOf(",");
      resolve(virgula >= 0 ? url.slice(virgula + 1) : "");
    };
    reader.onerror = () => reject(new Error("leitura"));
    reader.readAsDataURL(file);
  });
}

function mostrarCampos(tipo, lado) {
  if (!lado) return "";
  const partes = [];
  if (lado.net_value !== undefined && lado.net_value !== null) partes.push(formatCurrency(lado.net_value));
  if (lado.net_value === null) partes.push("-");
  if (lado.salary !== undefined && lado.salary !== null) partes.push(formatCurrency(lado.salary));
  if (lado.name !== undefined) partes.push(lado.name || "-");
  // `dados_bancarios` já vem mascarado do servidor; no `novo` mostra só o banco
  // (agência e conta em claro ficam só em memória, para o pré-preenchimento).
  if (tipo === "dados_bancarios") {
    partes.push([lado.bank_code, lado.bank_agency, lado.bank_account].filter(Boolean).join(" / "));
  } else if (lado.bank_code) {
    partes.push(lado.bank_code);
  }
  return partes.filter(Boolean).join(" · ");
}

const estadoInicial = () => ({
  etapa: "upload", // upload | preview | resultado
  competencia: brMonthStr(),
  arquivoB64: "",
  arquivoNome: "",
  preview: null,
  pendenciasArquivo: null,
  selecionados: {},
  complementos: {},
  datasSaida: {},
  massa: { department: "", bank_type: "" },
  sendNotification: false,
  progresso: null,
  resultado: null,
});

export default function EmployeeImportCnabDialog({ open, onOpenChange, onCadastrarManual, onApplied, departments = [] }) {
  const [st, setSt] = useState(estadoInicial);
  const [ocupado, setOcupado] = useState(false);
  const patch = (p) => setSt((s) => ({ ...s, ...p }));

  const fechar = (v) => {
    if (!v && ocupado) return; // não perde o estado no meio de um envio
    if (!v) setSt(estadoInicial());
    onOpenChange(v);
  };

  const itens = st.preview?.itens || [];
  const porId = Object.fromEntries(itens.map((i) => [i.item_id, i]));
  const selecionadosAplicaveis = itens.filter((i) => i.aplicavel && st.selecionados[i.item_id]);

  const escolherArquivo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const b64 = await lerComoBase64(file);
      patch({ arquivoB64: b64, arquivoNome: file.name, pendenciasArquivo: null });
    } catch {
      toast.error(COPY.erroArquivo);
    }
  };

  const gerarComparativo = async () => {
    setOcupado(true);
    try {
      const res = await api.post("/employees/import/cnab/preview", {
        arquivo_b64: st.arquivoB64, competencia: st.competencia,
      });
      const preview = res.data;
      const selecionados = {};
      const complementos = {};
      (preview.itens || []).forEach((i) => {
        selecionados[i.item_id] = !!(i.aplicavel && i.marcado_por_padrao);
        if (i.tipo === T_NOVO) complementos[i.item_id] = { ...COMPLEMENTO_VAZIO };
      });
      patch({ etapa: "preview", preview, selecionados, complementos, datasSaida: {}, pendenciasArquivo: null });
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 422 && Array.isArray(data?.pendencias)) {
        patch({ pendenciasArquivo: data });
      } else {
        toast.error(handleApiError(err, COPY.erroPreview));
      }
    }
    setOcupado(false);
  };

  const setComplemento = (itemId, campo, valor) => setSt((s) => ({
    ...s, complementos: { ...s.complementos, [itemId]: { ...(s.complementos[itemId] || COMPLEMENTO_VAZIO), [campo]: valor } },
  }));

  const aplicarMassa = () => setSt((s) => {
    const complementos = { ...s.complementos };
    (s.preview?.itens || []).forEach((i) => {
      if (i.tipo !== T_NOVO || !i.aplicavel || !s.selecionados[i.item_id]) return;
      const atual = complementos[i.item_id] || COMPLEMENTO_VAZIO;
      complementos[i.item_id] = {
        ...atual,
        ...(s.massa.department.trim() ? { department: s.massa.department.trim() } : {}),
        ...(s.massa.bank_type ? { bank_type: s.massa.bank_type } : {}),
      };
    });
    return { ...s, complementos };
  });

  const montarDecisao = (item) => {
    const d = { item_id: item.item_id, acao: "aplicar" };
    if (item.tipo === T_SAIDA) d.termination_date = st.datasSaida[item.item_id] || null;
    if (item.tipo === T_NOVO) {
      const c = st.complementos[item.item_id] || COMPLEMENTO_VAZIO;
      d.complemento = {
        email: c.email.trim() || null,
        phone: c.phone.trim() || null,
        department: c.department.trim() || null,
        salary: parseValorBR(c.salary),
        bank_type: c.bank_type || null,
      };
    }
    return d;
  };

  const aplicar = async () => {
    // Só as linhas marcadas vão como `aplicar`; o que não vai em `decisoes`
    // não é tocado (SPEC Contrato §2).
    const blocos = emBlocos(selecionadosAplicaveis.map(montarDecisao));
    if (!blocos.length) return;
    setOcupado(true);
    const itensResultado = [];
    const status = [];
    let falha = null;
    let naoEnviados = 0;
    let pendenciasArquivo = null;
    for (let b = 0; b < blocos.length; b += 1) {
      patch({ progresso: { feitos: b + 1, total: blocos.length } });
      try {
        const res = await api.post("/employees/import/cnab/apply", {
          arquivo_b64: st.arquivoB64,
          file_sha256: st.preview.file_sha256,
          competencia: st.preview.competencia,
          send_notification: st.sendNotification,
          decisoes: blocos[b],
        });
        itensResultado.push(...(res.data?.itens || []));
        status.push(res.data?.status);
      } catch (err) {
        // Para no 1.º bloco recusado: os blocos já aplicados ficam aplicados
        // (cada um tem o seu recibo); os seguintes não são enviados.
        falha = handleApiError(err, COPY.erroApply);
        const data = err?.response?.data;
        if (err?.response?.status === 422 && Array.isArray(data?.pendencias)) pendenciasArquivo = data;
        naoEnviados = blocos.slice(b).reduce((n, bl) => n + bl.length, 0);
        break;
      }
    }
    patch({ etapa: "resultado", progresso: null, pendenciasArquivo, resultado: { itens: itensResultado, status, falha, naoEnviados } });
    setOcupado(false);
    if (itensResultado.length && onApplied) onApplied();
  };

  const marcas = st.preview?.marcas || [];
  // Mesmo render para o 422 do preview e o do apply (pendências do arquivo).
  const caixaPendencias = st.pendenciasArquivo && (
    <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20 text-xs space-y-1" data-testid="import-cnab-pendencias">
      <p className="font-semibold text-red-400">{st.pendenciasArquivo.detail}</p>
      <p>{COPY.pendenciasArquivo}: {Object.entries(st.pendenciasArquivo.total_por_classe || {}).map(([k, n]) => `${k} (${n})`).join(", ")}</p>
      <ul className="list-disc pl-4">
        {st.pendenciasArquivo.pendencias.map((p, idx) => (
          <li key={`${p.tipo}-${p.id}-${p.campo}-${idx}`}>{[p.tipo, p.id, p.campo, p.motivo].filter(Boolean).join(" · ")}</li>
        ))}
      </ul>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={fechar}>
      <DialogContent className="sm:max-w-5xl border-white/10 max-h-[90vh] overflow-y-auto" data-testid="import-cnab-dialog">
        <DialogHeader>
          <DialogTitle>{COPY.titulo}</DialogTitle>
          <DialogDescription>{COPY.descricao}</DialogDescription>
        </DialogHeader>

        {st.etapa === "upload" && (
          <div className="space-y-4 py-2">
            <p className="text-xs text-muted-foreground">{COPY.notaArquivo}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="space-y-2 text-sm">
                <span>{COPY.arquivo}</span>
                <Input data-testid="import-cnab-file" type="file" onChange={escolherArquivo} className="bg-black/20 border-white/10" />
              </label>
              <label className="space-y-2 text-sm">
                <span>{COPY.competencia}</span>
                <Input data-testid="import-cnab-competencia" type="month" value={st.competencia}
                  onChange={(e) => patch({ competencia: e.target.value })} className="bg-black/20 border-white/10" />
              </label>
            </div>
            {caixaPendencias}
          </div>
        )}

        {st.etapa === "preview" && st.preview && (
          <div className="space-y-4 py-2">
            {(st.preview.ja_aplicado_em || marcas.length > 0) && (
              <div className="p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/20 text-xs text-yellow-400 space-y-1" data-testid="import-cnab-avisos">
                {st.preview.ja_aplicado_em && (
                  <p data-testid="import-cnab-aviso-ja_aplicado_em">{COPY.aviso}: {COPY.jaAplicadoEm} {formatDateTime(st.preview.ja_aplicado_em)}</p>
                )}
                {marcas.map((m) => <p key={m} data-testid={`import-cnab-aviso-${m}`}>{COPY.aviso}: {m}</p>)}
              </div>
            )}
            <p className="text-xs text-muted-foreground" data-testid="import-cnab-resumo">
              {COPY.resumo} ({st.preview.competencia}): {Object.entries(st.preview.resumo || {}).map(([t, n]) => `${ROTULO_TIPO[t] || t}: ${n}`).join(" · ")}
            </p>

            {itens.some((i) => i.tipo === T_NOVO && i.aplicavel) && (
              <div className="flex flex-wrap items-end gap-3 p-3 rounded-lg bg-black/10 border border-white/10">
                <p className="w-full text-xs font-semibold uppercase tracking-wider text-muted-foreground">{COPY.massaTitulo}</p>
                <label className="text-xs space-y-1">
                  <span>{COPY.department}</span>
                  <Input data-testid="import-cnab-massa-department" list="import-cnab-departamentos" value={st.massa.department} disabled={ocupado}
                    onChange={(e) => patch({ massa: { ...st.massa, department: e.target.value } })} className="h-8 bg-black/20 border-white/10 text-xs" />
                  <datalist id="import-cnab-departamentos">
                    {departments.map((d) => <option key={d} value={d} />)}
                  </datalist>
                </label>
                <label className="text-xs space-y-1">
                  <span>{COPY.bankType}</span>
                  <select data-testid="import-cnab-massa-bank-type" value={st.massa.bank_type} disabled={ocupado}
                    onChange={(e) => patch({ massa: { ...st.massa, bank_type: e.target.value } })}
                    className="block h-8 rounded-md bg-black/20 border border-white/10 px-2 text-xs">
                    <option value="">-</option>
                    {BANK_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <Button type="button" variant="outline" size="sm" data-testid="import-cnab-massa-aplicar" disabled={ocupado} onClick={aplicarMassa} className="border-white/10 text-xs">
                  {COPY.massaAplicar}
                </Button>
              </div>
            )}

            <div className="overflow-x-auto">
              <Table className="min-w-[900px] text-xs [&_td]:px-2 [&_td]:py-2 [&_th]:px-2 [&_th]:py-2">
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="w-8" />
                    <TableHead>{COPY.colTipo}</TableHead>
                    <TableHead>{COPY.colNome}</TableHead>
                    <TableHead>{COPY.colCpf}</TableHead>
                    <TableHead>{COPY.colAntes}</TableHead>
                    <TableHead>{COPY.colDepois}</TableHead>
                    <TableHead>{COPY.colAvisos}</TableHead>
                    <TableHead>{COPY.colCompletar}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {itens.map((item) => {
                    const comp = st.complementos[item.item_id] || COMPLEMENTO_VAZIO;
                    const marcado = !!st.selecionados[item.item_id];
                    const faltam = item.tipo === T_NOVO && item.aplicavel ? faltantesDoComplemento(comp) : [];
                    return (
                      <TableRow key={item.item_id} data-testid={`import-cnab-row-${item.item_id}`} className="border-white/5">
                        <TableCell>
                          {item.aplicavel && (
                            <Checkbox data-testid={`import-cnab-check-${item.item_id}`} checked={marcado} disabled={ocupado}
                              onCheckedChange={(v) => setSt((s) => ({ ...s, selecionados: { ...s.selecionados, [item.item_id]: v === true } }))} />
                          )}
                        </TableCell>
                        <TableCell>{ROTULO_TIPO[item.tipo] || item.tipo}</TableCell>
                        <TableCell>{item.nome}</TableCell>
                        <TableCell>{item.cpf_mascarado}</TableCell>
                        <TableCell>{mostrarCampos(item.tipo, item.antes)}</TableCell>
                        <TableCell>{mostrarCampos(item.tipo, item.depois)}</TableCell>
                        <TableCell>
                          {[...(item.marcas || []), ...(item.bloqueio?.motivo ? [item.bloqueio.motivo] : [])].join(", ")}
                        </TableCell>
                        <TableCell>
                          {item.tipo === T_NOVO && item.aplicavel && (
                            <div className="grid grid-cols-3 gap-1 min-w-[320px]">
                              <Input data-testid={`import-cnab-email-${item.item_id}`} type="email" placeholder={COPY.email} aria-label={COPY.email}
                                value={comp.email} disabled={ocupado} onChange={(e) => setComplemento(item.item_id, "email", e.target.value)} className="h-7 bg-black/20 border-white/10 text-xs" />
                              <Input data-testid={`import-cnab-phone-${item.item_id}`} placeholder={COPY.phone} aria-label={COPY.phone} maxLength={15}
                                value={comp.phone} disabled={ocupado} onChange={(e) => setComplemento(item.item_id, "phone", formatPhone(e.target.value))} className="h-7 bg-black/20 border-white/10 text-xs" />
                              <Input data-testid={`import-cnab-salary-${item.item_id}`} inputMode="decimal" placeholder={COPY.salary} aria-label={COPY.salary}
                                value={comp.salary} disabled={ocupado} onChange={(e) => setComplemento(item.item_id, "salary", e.target.value.replace(/[^\d.,]/g, ""))} className="h-7 bg-black/20 border-white/10 text-xs" />
                              <span className="col-span-3 text-[11px] text-muted-foreground" data-testid={`import-cnab-massa-valores-${item.item_id}`}>
                                {COPY.department}: {comp.department || "-"} · {COPY.bankType}: {comp.bank_type || "-"}
                                {faltam.length > 0 && <> · {COPY.faltando}: {faltam.map(rotuloCampo).join(", ")}</>}
                              </span>
                            </div>
                          )}
                          {item.tipo === T_SAIDA && item.aplicavel && (
                            <Input data-testid={`import-cnab-saida-date-${item.item_id}`} type="date" aria-label={COPY.dataSaida}
                              value={st.datasSaida[item.item_id] || ""} disabled={ocupado}
                              onChange={(e) => setSt((s) => ({ ...s, datasSaida: { ...s.datasSaida, [item.item_id]: e.target.value } }))}
                              className="h-7 bg-black/20 border-white/10 text-xs" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <label className="flex items-center gap-3 p-3 rounded-lg bg-blue-500/5 border border-blue-500/10 text-sm">
              <Checkbox data-testid="import-cnab-notify" checked={st.sendNotification} disabled={ocupado}
                onCheckedChange={(v) => patch({ sendNotification: v === true })} />
              <span>{COPY.enviarEmail}</span>
            </label>
            {st.progresso && (
              <p className="text-xs text-muted-foreground" data-testid="import-cnab-progresso">{COPY.aplicando(st.progresso.feitos, st.progresso.total)}</p>
            )}
          </div>
        )}

        {st.etapa === "resultado" && st.resultado && (
          <div className="space-y-3 py-2" data-testid="import-cnab-resultado">
            <p className="text-sm font-semibold">{COPY.resultado}: {st.resultado.status.join(", ")}</p>
            {st.resultado.falha && (
              <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20 text-xs text-red-400" data-testid="import-cnab-falha">
                <p>{COPY.naoEnviados(st.resultado.naoEnviados)}</p>
                <p>{st.resultado.falha}</p>
              </div>
            )}
            {caixaPendencias}
            <div className="overflow-x-auto">
              <Table className="min-w-[700px] text-xs [&_td]:px-2 [&_td]:py-2 [&_th]:px-2 [&_th]:py-2">
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead>{COPY.colTipo}</TableHead>
                    <TableHead>{COPY.colNome}</TableHead>
                    <TableHead>{COPY.colCpf}</TableHead>
                    <TableHead>{COPY.colEstado}</TableHead>
                    <TableHead>{COPY.colMotivo}</TableHead>
                    <TableHead>{COPY.colCampos}</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {st.resultado.itens.map((r) => {
                    const item = porId[r.item_id];
                    return (
                      <TableRow key={r.item_id} data-testid={`import-cnab-result-${r.item_id}`} className="border-white/5">
                        <TableCell>{item ? (ROTULO_TIPO[item.tipo] || item.tipo) : "-"}</TableCell>
                        <TableCell>{item?.nome || "-"}</TableCell>
                        <TableCell>{item?.cpf_mascarado || "-"}</TableCell>
                        <TableCell data-testid={`import-cnab-estado-${r.item_id}`}>{r.estado}</TableCell>
                        <TableCell>{r.motivo || ""}</TableCell>
                        <TableCell>{(r.campos || []).map(rotuloCampo).join(", ")}</TableCell>
                        <TableCell>
                          {r.estado === "pendente" && item?.tipo === T_NOVO && onCadastrarManual && (
                            <Button type="button" variant="outline" size="sm" data-testid={`import-cnab-manual-${r.item_id}`}
                              onClick={() => onCadastrarManual(prefillCadastroManual(item))} className="border-white/10 text-xs">
                              {COPY.cadastrarManual}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        <DialogFooter>
          {st.etapa === "upload" && (
            <>
              <Button variant="outline" onClick={() => fechar(false)} className="border-white/10">{COPY.cancelar}</Button>
              <Button data-testid="import-cnab-preview-btn" onClick={gerarComparativo} disabled={ocupado || !st.arquivoB64 || !st.competencia}
                className="bg-blue-600 hover:bg-blue-700 text-white">
                {ocupado ? <div className="loading-spinner" /> : COPY.gerarComparativo}
              </Button>
            </>
          )}
          {st.etapa === "preview" && (
            <>
              <Button variant="outline" data-testid="import-cnab-trocar" disabled={ocupado} onClick={() => setSt(estadoInicial())} className="border-white/10">{COPY.trocarArquivo}</Button>
              <Button data-testid="import-cnab-apply-btn" onClick={aplicar} disabled={ocupado || selecionadosAplicaveis.length === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white">
                {ocupado ? <div className="loading-spinner" /> : COPY.aplicar(selecionadosAplicaveis.length)}
              </Button>
            </>
          )}
          {st.etapa === "resultado" && (
            <Button data-testid="import-cnab-fechar" onClick={() => fechar(false)} className="bg-blue-600 hover:bg-blue-700 text-white">{COPY.fechar}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
